import React, { useState, useEffect, useMemo } from 'react'
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { 
  Play, 
  Loader2, 
  CheckCircle, 
  Clock, 
  User, 
  Calendar,
  AlertTriangle,
  ExternalLink,
  List,
  X,
  Check,
  CheckCheck,
  ArrowUp,
  ArrowDown,
  Star,
  Trophy,
  Award,
  Zap,
  Target,
  Flag,
  Heart,
  ThumbsUp,
  Smile,
  PartyPopper,
  ArrowBigRight,
  FileText,
  MessageSquare
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { formConditionEvaluators } from '@/lib/formFieldConditions'
import {
  normalizeFormFields,
  getMissingRequiredFields,
  buildVisibleFormPayload,
  getDateFieldRangeErrors,
} from '@/lib/workflowFormRuntime'
import { format, isBefore, startOfToday } from 'date-fns'

const MockExecutionModal = ({ 
  open, 
  onOpenChange, 
  workflow, 
  onExecute 
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [mockResult, setMockResult] = useState(null)
  const [error, setError] = useState(null)
  const [selectedTask, setSelectedTask] = useState(null)
  const [showTaskModal, setShowTaskModal] = useState(false)
  
  // State for post-completion form modal
  const [showPostCompletionForm, setShowPostCompletionForm] = useState(false)
  const [currentTaskForCompletion, setCurrentTaskForCompletion] = useState(null)
  const [formData, setFormData] = useState({})
  const [formFields, setFormFields] = useState([])
  const [isLoadingForm, setIsLoadingForm] = useState(false)
  const [formSettings, setFormSettings] = useState({})

  const { isFieldVisible, isFieldRequired } = useMemo(
    () => formConditionEvaluators(formData, formFields),
    [formData, formFields],
  )

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setMockResult(null)
      setError(null)
      setIsLoading(false)
      setSelectedTask(null)
      setShowTaskModal(false)
      
      if (workflow?.steps) {
        workflow.steps.forEach((step, index) => {
          console.log(`Step ${index}:`, {
            id: step.id,
            type: step.type,
            step_type: step.step_type,
            form_id: step.form_id,
            fields: step.fields?.length || 0,
            settings: step.settings
          });
        });
      }
    }
  }, [open, workflow])

  const handleMockExecute = async () => {
    if (!workflow?.id) return

    // Reset state completely
    setIsLoading(true)
    setError(null)
    setMockResult(null)

    try {
      const response = await fetch(`/workflows/${workflow.id}/mock-execute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content'),
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      const data = await response.json()

      if (data.success) {
        // The backend already returns the correct TaskNode data, so we just need to add workflow context
        const enhancedResult = {
          ...data.data,
          tasks: data.data.tasks?.map((mockTask) => {
            return {
              ...mockTask,
              // Add workflow context
              workflow_id: workflow.id,
              workflow_name: workflow.name,
            };
          }) || []
        };
        
        setMockResult(enhancedResult);
      } else {
        setError(data.message || 'Failed to execute mock workflow')
      }
    } catch (err) {
      setError('An error occurred while executing the mock workflow')
      console.error('Mock execution error:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const getPriorityColor = (priority) => {
    const priorityNum = Number.parseInt(priority, 10);
    if (priorityNum >= 8) {
      return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
    } else if (priorityNum >= 5) {
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400";
    } else {
      return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Completed":
        return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
      case "In Progress":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400";
      case "To Do":
        return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400";
      case "In Review":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400";
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm')
    } catch {
      return 'Invalid date'
    }
  }

  const handleTaskClick = (task) => {
    setSelectedTask(task)
    setShowTaskModal(true)
  }

  // Helper functions from task detail modal
  const formatDescription = (description) => {
    if (!description) return '';
    
    // Replace \n with <br> tags and handle both \n and </br> patterns
    let formatted = description
        .replace(/\\n/g, '<br><br>')
        .replace(/<\/br>/g, '<br><br>')
        .replace(/\n/g, '<br><br>');
    
    // Add word-break opportunities in URLs to allow them to wrap
    formatted = formatted.replace(/(https?\s*:\s*\/\/[^\s<]+)/g, (url) => {
        return url.replace(/([\/\.\?\&\=\%\-\_\:])/g, '$1<wbr>');
    });
    return formatted;
  };

  const formatTaskDate = (date) => {
    if (!date) return 'No due date';
    
    try {
      // Parse the date and treat it as local time to avoid timezone issues
      let taskDate;
      if (typeof date === 'string') {
        if (date.includes('T')) {
          // If it's an ISO string, extract just the date part to avoid timezone conversion
          const datePart = date.split('T')[0];
          taskDate = new Date(datePart + 'T12:00:00'); // Use noon to avoid timezone edge cases
        } else {
          // If it's a date-only string (Y-m-d format), parse it as local date
          taskDate = new Date(date + 'T12:00:00'); // Use noon to avoid timezone edge cases
        }
      } else {
        taskDate = new Date(date);
      }
      
      const today = startOfToday();
      
      if (isBefore(taskDate, today)) {
        return `Overdue - ${format(taskDate, 'MMM dd, yyyy')}`;
      } else if (format(taskDate, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd')) {
        return `Today - ${format(taskDate, 'MMM dd, yyyy')}`;
      } else {
        return format(taskDate, 'MMM dd, yyyy');
      }
    } catch (error) {
      return 'Invalid date';
    }
  };

  // Helper function to resolve variables in text
  const resolveVariables = (text, extractedVariables = {}) => {
    if (!text || typeof text !== 'string') return text
    
    let resolvedText = text
    
    // Replace {{variable}} patterns with actual values
    Object.keys(extractedVariables).forEach(key => {
      const value = extractedVariables[key]
      if (value !== null && value !== undefined) {
        const pattern = new RegExp(`\\{\\{${key}\\}\\}`, 'g')
        resolvedText = resolvedText.replace(pattern, String(value))
      }
    })
    
    return resolvedText
  };

  const getEffectiveDueDate = (task) => {
    if (!task) return null;
    return task.defer_date ? task.defer_date : task.due;
  };

  const isOverdue = (task) => {
    if (!task || !task.due) return false;
    const dueDate = new Date(task.due);
    const today = startOfToday();
    return isBefore(dueDate, today) && task.status !== 'Completed';
  };

  // Helper function to get completion button settings from task
  const getCompletionSettings = (task) => {
    const settings = task?.settings || {};
    
    const result = {
      completionButtonText: settings.completion_button_text || "Complete Task",
      completionButtonColor: settings.completion_button_color || "#10b981",
      completionButtonIcon: settings.completion_button_icon || "CheckCircle",
      requirePostCompletionForm: settings.require_post_completion_form || false,
      postCompletionFormId: settings.post_completion_form_id || null,
      captureDeferDateInPostCompletionForm:
        settings.capture_defer_date_in_post_completion_form || false,
      postCompletionDeferDateFieldId:
        settings.post_completion_defer_date_field_id || null,
    };
    return result;
  };

  // Icon mapping for completion button
  const iconMap = {
    CheckCircle,
    Check,
    CheckCheck,
    Star,
    Trophy,
    Award,
    Zap,
    Target,
    Flag,
    Heart,
    ThumbsUp,
    Smile,
    PartyPopper,
    ArrowBigRight,
  };

  const postCompletionSubmitSettings = useMemo(() => {
    const settings = formSettings || {}
    return {
      submitButtonText:
        settings.submitButtonText || settings.submit_button_text || "Submit Form",
      submitButtonColor:
        settings.submitButtonColor || settings.submit_button_color || "#3b82f6",
      submitButtonIcon:
        settings.submitButtonIcon || settings.submit_button_icon || "CheckCircle",
      resetButtonText:
        settings.resetButtonText || settings.reset_button_text || "Cancel",
    }
  }, [formSettings])

  // Handle task completion with post-completion form check
  const handleTaskCompletion = async (task) => {
    const completionSettings = getCompletionSettings(task);
    
    // If post-completion form is required, show the form first
    if (completionSettings.requirePostCompletionForm && completionSettings.postCompletionFormId) {
      setCurrentTaskForCompletion(task);
      await loadFormFields(completionSettings.postCompletionFormId);
      setShowPostCompletionForm(true);
      return;
    }

    // If this is a form-generated task, use its form data directly
    if (task.is_form_generated && task.form_fields) {
      setCurrentTaskForCompletion(task);
      setFormFields(normalizeFormFields(task.form_fields));
      setShowPostCompletionForm(true);
      return;
    }

    // If this is a form-generated task but no form_fields, try to find the form step
    if (task.is_form_generated) {
      const formStep = workflow?.steps?.find(step => 
        step.type === 'formNode' && step.step_id === task.step_id
      );
      
      if (formStep && formStep.fields) {
        setCurrentTaskForCompletion(task);
        setFormFields(normalizeFormFields(formStep.fields));
        setShowPostCompletionForm(true);
        return;
      }
    }

    // If we have a post-completion form ID but no workflow steps, try to load from API directly
    if (completionSettings.requirePostCompletionForm && completionSettings.postCompletionFormId) {
      setCurrentTaskForCompletion(task);
      await loadFormFields(completionSettings.postCompletionFormId);
      setShowPostCompletionForm(true);
      return;
    }

    // Check if this is a form task that needs completion simulation
    if (task.step_type === 'form' || task.is_form_generated) {
      // Find the form step to get the form fields
      const formStep = workflow?.steps?.find(step => 
        step.type === 'formNode' && step.step_id === task.step_id
      );
      
      if (formStep && formStep.fields) {
        setCurrentTaskForCompletion(task);
        setFormFields(normalizeFormFields(formStep.fields));
        setShowPostCompletionForm(true);
        return;
      }
    }

    // Otherwise, just close the modal (in real implementation, this would complete the task)
    setShowTaskModal(false);
  };

  // Load form fields from the form node
  const loadFormFields = async (formId) => {
    if (!formId) {
      return;
    }
    
    setIsLoadingForm(true);
    try {
      // First, try to find the form in the workflow steps
      const formStep = workflow?.steps?.find(step => {
        return step.type === 'formNode' && step.form_id == formId;
      });
      
      if (formStep && formStep.fields) {
        setFormFields(normalizeFormFields(formStep.fields));
        setFormSettings(formStep.settings || {});
        setIsLoadingForm(false);
        return;
      }
      
      // If not found in workflow steps, try to load from API
      const response = await fetch(`/forms/${formId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load form: ${response.status}`);
      }

      const formData = await response.json();
      
      // Handle different possible field structures
      let fields = [];
      let settings = {};
      
      if (formData.success && formData.form && formData.form.fields && Array.isArray(formData.form.fields)) {
        fields = formData.form.fields;
        settings = formData.form.settings || {};
      } else if (formData.fields && Array.isArray(formData.fields)) {
        fields = formData.fields;
        settings = formData.settings || {};
      } else if (formData.data && formData.data.fields && Array.isArray(formData.data.fields)) {
        fields = formData.data.fields;
        settings = formData.data.settings || {};
      }
      
      setFormFields(fields);
      setFormSettings(settings);
    } catch (error) {
      setFormFields([]);
    } finally {
      setIsLoadingForm(false);
    }
  };

  const handleFormSubmit = async () => {
    const missing = getMissingRequiredFields(formFields, formData)
    if (missing.length > 0) {
      setError(
        `Please fill required fields: ${missing.map((m) => m.label).join(', ')}`,
      )
      return
    }

    const { isFieldVisible } = formConditionEvaluators(formData, formFields)
    const dateErrors = getDateFieldRangeErrors(formFields, formData, isFieldVisible)
    if (Object.keys(dateErrors).length > 0) {
      setError(Object.values(dateErrors).join('; '))
      return
    }

    const payload = buildVisibleFormPayload(formFields, formData)

    const isFormTask = currentTaskForCompletion && (
      currentTaskForCompletion.is_form_generated || 
      currentTaskForCompletion.step_type === 'form' ||
      currentTaskForCompletion.step_id // If we have a step_id, try the conditional path
    );
    
    if (isFormTask) {
      await handleFormCompletionWithConditions(payload);
    } else {
      // Regular form submission
      setShowPostCompletionForm(false);
      setCurrentTaskForCompletion(null);
      setFormData({});
      setFormFields([]);
      setFormSettings({});
      // Close the task modal and return to main view
      setShowTaskModal(false);
    }
  };

  // Handle form completion with condition evaluation
  const handleFormCompletionWithConditions = async (formData) => {
    try {
      setIsLoading(true);
      
      // Call backend to evaluate conditions and create conditional tasks
      const response = await fetch(`/workflows/${workflow.id}/mock-form-completion`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content'),
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          formData: formData,
          formStepId: currentTaskForCompletion.step_id,
          existingTasks: mockResult.tasks || []
        })
      });

      const data = await response.json();

      if (data.success) {
        // Update the mock result with new conditional tasks
        setMockResult(prevResult => ({
          ...prevResult,
          tasks: [...(prevResult.tasks || []), ...(data.data.conditionalTasks || [])],
          tasks_count: (prevResult.tasks || []).length + (data.data.conditionalTasks || []).length
        }));
        
        // Close the task modal and return to main view
        setShowTaskModal(false);
      } else {
        setError(data.message || 'Failed to evaluate conditions');
      }
    } catch (err) {
      setError('An error occurred while evaluating conditions');
    } finally {
      setIsLoading(false);
      setShowPostCompletionForm(false);
      setCurrentTaskForCompletion(null);
      setFormData({});
      setFormFields([]);
      setFormSettings({});
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-none sm:max-w-[600px] lg:max-w-[800px] h-full sm:h-[600px] lg:h-5/6 lg:max-h-[800px] flex flex-col overflow-hidden">
        <DialogHeader className=" flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-xl font-semibold">
                Mock Workflow Execution
              </DialogTitle>
              <DialogDescription className="mt-1">
                Preview the tasks that would be created when this workflow runs
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1 w-full">
          <style dangerouslySetInnerHTML={{
            __html: `
              [data-radix-scroll-area-viewport] > div {
                height: 100% !important;
                display: flex !important;
                flex-direction: column !important;
              }
            `
          }} />
          <div className="pt-0 space-y-6 p-1 h-full flex flex-col">
          {!mockResult && !error && (
            <div className="flex items-center justify-center py-8 flex-1">
              <div className="text-center py-8">
                <div className="mx-auto w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mb-4">
                  <Play className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                  Ready to Test
                </h3>
                <p className="text-gray-500 dark:text-gray-400 mb-6">
                  Click the button below to simulate workflow execution and see what tasks would be created.
                </p>
                <Button 
                  onClick={handleMockExecute}
                  disabled={isLoading}
                  className="px-6 dark:text-white"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Executing...
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4 mr-2" />
                      Run Mock Execution
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center py-8">
              <div className="text-center py-8">
                <div className="mx-auto w-12 h-12 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mb-4">
                  <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                  Execution Failed
                </h3>
                <p className="text-gray-500 dark:text-gray-400 mb-6">
                  {error}
                </p>
                <Button 
                  onClick={handleMockExecute}
                  disabled={isLoading}
                  variant="outline"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Retrying...
                    </>
                  ) : (
                    'Try Again'
                  )}
                </Button>
              </div>
            </div>
          )}

          {mockResult && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Execution Summary
                  </h3>
                  <Badge variant="outline" className="text-sm bg-blue-50 dark:bg-blue-900/20 rounded-lg text-blue-900 dark:text-blue-100">
                    {mockResult.tasks_count} task{mockResult.tasks_count !== 1 ? 's' : ''} would be created
                  </Badge>
                  {mockResult.messages_count > 0 && (
                    <Badge variant="outline" className="text-sm bg-pink-50 dark:bg-pink-900/20 rounded-lg text-pink-900 dark:text-pink-100 ml-2">
                      {mockResult.messages_count} SMS message{mockResult.messages_count !== 1 ? 's' : ''} simulated
                    </Badge>
                  )}
                </div>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Workflow:</span>
                      <span className="ml-2 font-medium text-gray-900 dark:text-gray-100">
                        {mockResult.workflow_name}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Trigger Type:</span>
                      <span className="ml-2 font-medium text-gray-900 dark:text-gray-100 capitalize">
                        {mockResult.trigger_type}
                      </span>
                    </div>
                  </div>
                  
                  {/* Request Details */}
                  {mockResult.tasks && mockResult.tasks.length > 0 && mockResult.tasks[0].is_request_generated && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                      <div className="flex items-start space-x-2">
                        <div className="flex-shrink-0">
                          <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-1">
                            HTTP Request Made
                          </h4>
                          <p className="text-xs text-blue-700 dark:text-blue-300">
                            The workflow executed an HTTP request to fetch data. The tasks below are based on the response received at this specific time.
                          </p>
                          <div className="mt-2 text-xs text-blue-600 dark:text-blue-400">
                            <strong>Note:</strong> Results may vary if the request is made at a different time, as the API may return different data.
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {mockResult.skipped_items && mockResult.skipped_items.length > 0 && (
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                  <div className="flex items-start space-x-2">
                    <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-amber-900 dark:text-amber-100 mb-2">
                        Skipped rows ({mockResult.skipped_items_count ?? mockResult.skipped_items.length})
                      </h4>
                      <ul className="space-y-2 text-xs text-amber-800 dark:text-amber-200">
                        {mockResult.skipped_items.map((item, skipIndex) => (
                          <li key={skipIndex} className="leading-relaxed">
                            {item.message}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Simulated SMS messages */}
              {mockResult.messages && mockResult.messages.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    <MessageSquare className="h-5 w-5 text-pink-500" />
                    Simulated SMS Messages
                  </h3>
                  <div className="grid gap-3">
                    {mockResult.messages.map((message, index) => {
                      const unresolvedAll = [
                        ...(message.unresolved_tokens?.recipient_phone || []),
                        ...(message.unresolved_tokens?.body || []),
                        ...(message.unresolved_tokens?.media_urls || []),
                      ]
                      const statusBadge = (() => {
                        if (message.status === 'blocked') {
                          return (
                            <Badge variant="outline" className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                              Blocked
                            </Badge>
                          )
                        }
                        if (message.status === 'deferred') {
                          return (
                            <Badge variant="outline" className="bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                              Deferred
                            </Badge>
                          )
                        }
                        return (
                          <Badge variant="outline" className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300">
                            Would send
                          </Badge>
                        )
                      })()

                      return (
                        <div key={`${message.step_id}-${index}`} className="border rounded-lg p-4 bg-white dark:bg-gray-900">
                          <div className="flex items-start justify-between mb-2 gap-2">
                            <div>
                              <p className="font-medium text-sm">{message.step_name || 'Send SMS'}</p>
                              <p className="text-xs text-muted-foreground">To: {message.to}</p>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {statusBadge}
                              <Badge variant="outline">{message.segments} segment{message.segments !== 1 ? 's' : ''}</Badge>
                            </div>
                          </div>

                          {message.gated_by && (
                            <div className="mb-2 rounded border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-200 text-xs px-3 py-2">
                              Waits for task <strong>{message.gated_by.step_name || `#${message.gated_by.step_id}`}</strong> to be completed before sending.
                            </div>
                          )}

                          {unresolvedAll.length > 0 && (
                            <div className="mb-2 rounded border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 text-red-900 dark:text-red-200 text-xs px-3 py-2">
                              <div className="font-medium mb-1">Unresolved variables &mdash; this SMS will be skipped:</div>
                              <div className="font-mono break-all">{unresolvedAll.join('  ·  ')}</div>
                            </div>
                          )}

                          <pre className="whitespace-pre-wrap text-sm bg-muted/30 rounded p-3">{message.body}</pre>
                          {message.media_urls?.length > 0 && (
                            <div className="mt-2 text-xs text-muted-foreground">
                              MMS: {message.media_urls.join(', ')}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {mockResult.tasks && mockResult.tasks.length > 0 ? (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center">
                    <List className="h-5 w-5 mr-2" />
                    Tasks That Would Be Created
                  </h3>
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-900 dark:text-gray-100">
                            Task Name
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {mockResult.tasks.map((task, index) => (
                          <tr 
                            key={index}
                            className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                            onClick={() => handleTaskClick(task)}
                          >
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {resolveVariables(task.name, task.extracted_variables || {})}
                                </div>
                                {task.condition_met && (
                                  <Badge
                                    variant="outline"
                                    className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 text-xs"
                                  >
                                    Conditional
                                  </Badge>
                                )}
                                {task.is_form_generated && (
                                  <Badge
                                    variant="outline"
                                    className="bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400 text-xs"
                                  >
                                    Form Task
                                  </Badge>
                                )}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {task.condition_met ? 
                                  `Created when condition "${task.condition_description}" was met` : 
                                  'Click to view detailed preview'
                                }
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="mx-auto w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                    <CheckCircle className="h-6 w-6 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                    No Tasks Would Be Created
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    This workflow would not create any tasks with the current configuration.
                  </p>
                </div>
              )}
            </div>
          )}
          </div>
        </ScrollArea>

        <DialogFooter className="pt-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center justify-between w-full">
            <div className="text-xs text-gray-500 dark:text-gray-400">
              This is a preview only. No actual tasks will be created.
            </div>
            <div className="flex space-x-2">
              <Button 
                variant="outline" 
                onClick={() => onOpenChange(false)}
              >
                Close
              </Button>
              {!mockResult && !error && (
                <Button 
                  onClick={handleMockExecute}
                  disabled={isLoading}
                  className="dark:text-white"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Executing...
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4 mr-2" />
                      Run Mock
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </DialogFooter>
      </DialogContent>

      {/* Task Detail Modal - EXACTLY matching task-detail-modal.jsx */}
      <Dialog open={showTaskModal} onOpenChange={setShowTaskModal}>
        <DialogContent className="w-full max-w-none sm:max-w-[600px] lg:max-w-[800px] h-full sm:h-[600px] lg:h-5/6 lg:max-h-[800px] flex flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {resolveVariables(selectedTask?.name, selectedTask?.extracted_variables || {})}
              {selectedTask?.is_form_generated && (
                <Badge
                  variant="outline"
                  className="bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400"
                >
                  Form Task
                </Badge>
              )}
              {selectedTask?.condition_met && (
                <Badge
                  variant="outline"
                  className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                >
                  Conditional
                </Badge>
              )}
              {selectedTask && isOverdue(selectedTask) && (
                <Badge
                  variant="outline"
                  className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                >
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  Overdue
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Task preview - this is how the task would appear if created
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="details" className="flex-1 flex flex-col overflow-hidden">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger
                value="checklist"
                className="flex items-center"
              >
                Checklist
                {selectedTask?.checklist?.length > 0 && (
                  <Badge
                    variant="secondary"
                    className="ml-1.5 h-5 px-1.5 flex items-center justify-center"
                  >
                    {
                      selectedTask.checklist.filter(
                        (item) => item.checked
                      ).length
                    }
                    /{selectedTask.checklist.length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger
                value="notes"
                className="flex items-center"
              >
                Notes
                {selectedTask?.notes?.length > 0 && (
                  <Badge
                    variant="secondary"
                    className="ml-1.5 h-5 px-1.5 flex items-center justify-center"
                  >
                    {selectedTask.notes.length}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            <style dangerouslySetInnerHTML={{
              __html: `
                [role="tabpanel"][data-state="inactive"] {
                  display: none !important;
                }
              `
            }} />

            <TabsContent
              value="details"
              className="flex-1 overflow-hidden mt-4 px-1"
            >
              <ScrollArea className="pr-4 h-full overflow-y-auto">
                <div className="space-y-6 pr-4 pb-8">
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-1">
                      Description
                    </h3>
                    <div
                      className="text-sm whitespace-pre-wrap break-words"
                      dangerouslySetInnerHTML={{
                        __html: formatDescription(resolveVariables(selectedTask?.description || '', selectedTask?.extracted_variables || {}))
                      }}
                    />
                  </div>


                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <h3 className="text-sm font-medium text-muted-foreground mb-1">
                        Status
                      </h3>
                      <div className="flex items-center gap-2">
                        <Badge
                          variant="outline"
                          className={cn(
                            "justify-start",
                            getStatusColor(selectedTask?.status)
                          )}
                        >
                          {selectedTask?.status || 'To Do'}
                        </Badge>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-muted-foreground mb-1">
                        Priority
                      </h3>
                      <Badge
                        variant="outline"
                        className={cn(
                          "justify-start",
                          getPriorityColor(selectedTask?.priority || 5)
                        )}
                      >
                        {selectedTask?.priority || 5}
                      </Badge>
                    </div>

                    <div>
                      <h3 className="text-sm font-medium text-muted-foreground mb-1">
                        Category
                      </h3>
                      <span className="text-sm">
                        {selectedTask?.category || 'General'}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-muted-foreground mb-1">
                        Deferrable
                      </h3>
                      <span className="text-sm">
                        {selectedTask?.deferrable ? 'Yes' : 'No'}
                      </span>
                    </div>

                    <div>
                      <h3 className="text-sm font-medium text-muted-foreground mb-1">
                        Assignee{selectedTask?.assigned_user_names?.length > 1 ? 's' : ''}
                      </h3>
                      <div className="flex flex-col gap-2">
                        {selectedTask?.assigned_user_names?.length > 0 ? (
                          selectedTask.assigned_user_names.map((name, index) => (
                            <div key={`${name}-${index}`} className="flex items-center gap-2">
                              <Avatar className="h-8 w-8 border-2 border-white shadow-sm bg-primary p-4 text-white">
                                <AvatarFallback className="text-xs font-medium">
                                  {name
                                    .split(" ")
                                    .map((n) => n[0])
                                    .join("")}
                                </AvatarFallback>
                              </Avatar>
                              <span className="text-sm">{name}</span>
                            </div>
                          ))
                        ) : selectedTask?.assignee_id ? (
                          <div className="relative group">
                            <Avatar className="h-8 w-8 border-2 border-white shadow-sm hover:shadow-md transition-shadow hover:cursor-default bg-primary p-4 text-white">
                              <AvatarImage
                                src="/stylized-jd-initials.png"
                                alt="Assignee"
                              />
                              <AvatarFallback className="text-xs font-medium">
                                {selectedTask.assignee_name
                                  ? selectedTask.assignee_name
                                        .split(" ")
                                        .map((n) => n[0])
                                        .join("")
                                  : "U"}
                              </AvatarFallback>
                            </Avatar>
                            <div className="absolute bottom-full mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10 tooltip-left">
                              User ID: {selectedTask.assignee_id}
                              <div className="absolute top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 tooltip-left"></div>
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">
                            Unassigned
                          </span>
                        )}
                        {selectedTask?.assignment_type === 'external_id' && selectedTask?.assignment_resolution && (
                          <p className="text-xs text-muted-foreground">
                            External ID: {selectedTask.assignment_resolution.resolved_external_id || '(not resolved in mock)'}
                            {selectedTask.assignment_resolution.matched === false && ' — no match, workflow owner used'}
                          </p>
                        )}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-muted-foreground mb-1">
                        Due Date
                      </h3>
                      <div className="text-sm">
                        {formatTaskDate(getEffectiveDueDate(selectedTask))}
                        {selectedTask?.defer_date && (
                          <span className="ml-2 text-muted-foreground">
                            (Deferred)
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Task Type Categories - only show if categories exist */}
                    {selectedTask?.taskTypeCategories &&
                        selectedTask.taskTypeCategories.length > 0 && (
                            <div>
                                <h3 className="text-sm font-medium text-muted-foreground mb-1">
                                    Task Type Categories
                                </h3>
                                <div className="flex flex-wrap gap-2">
                                    {selectedTask.taskTypeCategories.map(
                                        (cat) => {
                                            // Match the color and label from task-form-modal
                                            const options = {
                                                quality: {
                                                    label: "Quality",
                                                    color: "#10b981",
                                                },
                                                speed: {
                                                    label: "Speed",
                                                    color: "#3b82f6",
                                                },
                                                procedural: {
                                                    label: "Procedural",
                                                    color: "#8b5cf6",
                                                },
                                            };
                                            const opt = options[
                                                cat
                                            ] || {
                                                label: cat,
                                                color: "#6b7280",
                                            };
                                            return (
                                                <span
                                                    key={cat}
                                                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-white rounded-full"
                                                    style={{
                                                        backgroundColor:
                                                            opt.color,
                                                    }}
                                                >
                                                    {opt.label}
                                                </span>
                                            );
                                        }
                                    )}
                                </div>
                            </div>
                        )}

                    {selectedTask?.link && (
                      <div>
                        <h3 className="text-sm font-medium text-muted-foreground mb-1">
                          Link
                        </h3>
                        <a
                          href={selectedTask.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-primary hover:underline break-all"
                        >
                          {selectedTask.link_title
                            ? selectedTask.link_title
                            : selectedTask.link}
                        </a>
                      </div>
                    )}

                    <div>
                      <h3 className="text-sm font-medium text-muted-foreground mb-1">
                        ID
                      </h3>
                      <div className="text-sm font-mono">
                        {selectedTask?.id || 'Preview Task'}
                      </div>
                    </div>
                  </div>
                </div>
              </ScrollArea>
            </TabsContent>

            <TabsContent
              value="checklist"
              className="flex-1 flex flex-col overflow-hidden mt-4"
            >
              <ScrollArea className="overflow-y-auto overflow-x-hidden">
                <div className="p-4">
                  {selectedTask?.checklist && selectedTask.checklist.length > 0 ? (
                    <div className="space-y-3">
                      {selectedTask.checklist.map((item, itemIndex) => (
                        <div key={itemIndex} className="flex items-start space-x-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                          <div className="w-5 h-5 border border-gray-300 dark:border-gray-600 rounded flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-gray-900 dark:text-gray-100">
                              {item.label}
                            </div>
                            {item.description && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {item.description}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="mx-auto w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                        <CheckCircle className="h-6 w-6 text-gray-400" />
                      </div>
                      <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                        No Checklist Items
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        This task would not have any checklist items.
                      </p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>

            <TabsContent
              value="notes"
              className="flex-1 flex flex-col overflow-hidden mt-4"
            >
              <ScrollArea className="overflow-y-auto overflow-x-hidden">
                <div className="flex items-center justify-end my-2 px-1">
                  <button
                    type="button"
                    className="flex items-center gap-1 px-2 py-1 rounded hover:bg-muted transition-colors border border-transparent hover:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                    title="Sort by date (Newest first)"
                  >
                    <ArrowDown className="w-4 h-4" />
                    <span className="text-xs text-muted-foreground">
                      Newest
                    </span>
                  </button>
                </div>
                <div className="space-y-4 pb-8">
                  <div className="flex flex-col items-center justify-center h-full text-center pb-4">
                    <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center mb-2">
                      <span className="text-muted-foreground text-lg">
                        📝
                      </span>
                    </div>
                    <h3 className="text-lg font-medium">
                      No notes yet
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      This is a preview task - notes would be added after creation
                    </p>
                  </div>
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>

           <DialogFooter className="flex flex-col gap-2 border-t pt-4 mt-auto bg-background">
             <div className="flex items-center justify-between w-full">
               <div className="text-xs text-muted-foreground">
                 This is a preview of how the task would appear if created
               </div>
               <div className="flex gap-2">
                 {(() => {
                   const completionSettings = getCompletionSettings(selectedTask);
                   const CompletionIcon = iconMap[completionSettings.completionButtonIcon] || CheckCircle;
                   
                   return (
                     <Button 
                       onClick={() => handleTaskCompletion(selectedTask)}
                       className="text-white"
                       style={{
                         backgroundColor: completionSettings.completionButtonColor,
                         borderColor: completionSettings.completionButtonColor,
                       }}
                     >
                       <CompletionIcon className="mr-2 h-4 w-4" />
                       {completionSettings.completionButtonText}
                     </Button>
                   );
                 })()}
                 <Button 
                   variant="outline" 
                   onClick={() => setShowTaskModal(false)}
                 >
                   Close
                 </Button>
               </div>
             </div>
           </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Post-Completion Form Modal */}
      {showPostCompletionForm && currentTaskForCompletion && (
        <Dialog
          open={showPostCompletionForm}
          onOpenChange={setShowPostCompletionForm}
        >
          <DialogContent className="w-full max-w-none sm:max-w-[600px] lg:max-w-[800px] h-full sm:h-[600px] lg:h-5/6 lg:max-h-[800px] flex flex-col overflow-hidden">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Complete Task: {currentTaskForCompletion.name}
              </DialogTitle>
              <DialogDescription>
                Please fill out the required information before completing this task.
              </DialogDescription>
            </DialogHeader>

            <div className="flex-1 overflow-hidden">
              {isLoadingForm ? (
                <div className="flex items-center justify-center h-full">
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="h-8 w-8 animate-spin" />
                    <span className="text-lg">Loading form...</span>
                  </div>
                </div>
              ) : (
                <ScrollArea className="h-full">
                  <div className="space-y-4 p-4">
                    {formFields.length === 0 ? (
                      <div className="text-center py-8">
                        <div className="mx-auto w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                          <FileText className="h-6 w-6 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                          No Form Fields Found
                        </h3>
                        <p className="text-gray-500 dark:text-gray-400">
                          The form does not have any fields configured.
                        </p>
                      </div>
                    ) : (
                      formFields.map((field, index) => {
                        if (!isFieldVisible(field)) return null
                        const effectiveRequired = isFieldRequired(field)
                        return (
                          <div key={field.id || index} className="space-y-2">
                            <label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {field.label}
                              {effectiveRequired && <span className="text-red-500 ml-1">*</span>}
                            </label>
                        
                        {field.type === 'text' && (
                          <input
                            type="text"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                            value={formData[field.id] || ''}
                            required={effectiveRequired}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              [field.id]: e.target.value
                            }))}
                          />
                        )}
                        
                        {field.type === 'textarea' && (
                          <textarea
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                            rows={3}
                            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                            value={formData[field.id] || ''}
                            required={effectiveRequired}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              [field.id]: e.target.value
                            }))}
                          />
                        )}
                        
                        {field.type === 'select' && (
                          <select
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                            value={formData[field.id] || ''}
                            required={effectiveRequired}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              [field.id]: e.target.value
                            }))}
                          >
                            <option value="">Select an option</option>
                            {field.options?.map((option, optIndex) => (
                              <option key={optIndex} value={option.value || option}>
                                {option.label || option}
                              </option>
                            ))}
                          </select>
                        )}
                        
                        {field.type === 'checkbox' && (
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id={field.id}
                              className="h-4 w-4 rounded border-gray-300 bg-background text-blue-600 dark:border-gray-600 dark:[color-scheme:dark]"
                              checked={formData[field.id] || false}
                              required={effectiveRequired}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                [field.id]: e.target.checked
                              }))}
                            />
                            <label htmlFor={field.id} className="text-sm text-gray-700 dark:text-gray-300">
                              {field.label}
                            </label>
                          </div>
                        )}

                        {field.type === 'number' && (
                          <input
                            type="number"
                            className="w-full rounded-md border border-gray-300 bg-background px-3 py-2 text-sm text-foreground [color-scheme:light] dark:border-gray-600 dark:[color-scheme:dark]"
                            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                            value={formData[field.id] || ''}
                            required={effectiveRequired}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              [field.id]: e.target.value
                            }))}
                            min={field.validation?.min}
                            max={field.validation?.max}
                            step={field.validation?.step || "1"}
                          />
                        )}

                        {field.type === 'email' && (
                          <input
                            type="email"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                            value={formData[field.id] || ''}
                            required={effectiveRequired}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              [field.id]: e.target.value
                            }))}
                          />
                        )}

                        {field.type === 'date' && (
                          <input
                            type="date"
                            className="w-full cursor-pointer rounded-md border border-gray-300 bg-background px-3 py-2 text-sm text-foreground [color-scheme:light] disabled:cursor-not-allowed dark:border-gray-600 dark:[color-scheme:dark]"
                            value={formData[field.id] || ''}
                            required={effectiveRequired}
                            min={field.validation?.minDate || undefined}
                            max={field.validation?.maxDate || undefined}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              [field.id]: e.target.value
                            }))}
                          />
                        )}
                        </div>
                      );
                    })
                    )}
                  </div>
                </ScrollArea>
              )}
            </div>

            <DialogFooter className="flex justify-between items-center border-t pt-4">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                This form would be submitted in the actual workflow
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowPostCompletionForm(false)
                    setCurrentTaskForCompletion(null)
                    setFormData({})
                    setFormFields([])
                    setFormSettings({})
                  }}
                >
                  {postCompletionSubmitSettings.resetButtonText}
                </Button>
                <Button
                  onClick={handleFormSubmit}
                  className="text-white"
                  style={{
                    backgroundColor: postCompletionSubmitSettings.submitButtonColor,
                    borderColor: postCompletionSubmitSettings.submitButtonColor,
                  }}
                >
                  {postCompletionSubmitSettings.submitButtonText}
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  )
}

export default MockExecutionModal